import React, { createContext, useContext } from 'react'
import { Platform } from 'react-native'
import { readPushStateSync, type PushState } from '@/lib/onesignal-push'
import {
    usePushSelfHeal,
    type PushRegistrationSnapshot,
    type ServerRegistrationStatus,
} from '@/hooks/usePushSelfHeal'

export type { ServerRegistrationStatus }

type PushRegistrationContextValue = PushRegistrationSnapshot & {
    heal: (options?: { requestPermission?: boolean; forceServerSync?: boolean }) => Promise<PushState | null>
}

const WEB_STUB: PushRegistrationContextValue = {
    localState: readPushStateSync(),
    serverStatus: 'idle',
    locallyHealthy: false,
    isFullyHealthy: false,
    isHealing: false,
    isVerifyingServer: false,
    lastHealedAt: null,
    heal: async () => null,
}

const PushRegistrationContext = createContext<PushRegistrationContextValue>(WEB_STUB)

function NativePushRegistrationProvider({ children }: { children: React.ReactNode }) {
    const value = usePushSelfHeal()
    return (
        <PushRegistrationContext.Provider value={value}>
            {children}
        </PushRegistrationContext.Provider>
    )
}

export function PushRegistrationProvider({ children }: { children: React.ReactNode }) {
    if (Platform.OS === 'web') {
        return (
            <PushRegistrationContext.Provider value={WEB_STUB}>
                {children}
            </PushRegistrationContext.Provider>
        )
    }

    return <NativePushRegistrationProvider>{children}</NativePushRegistrationProvider>
}

export function usePushRegistration(): PushRegistrationContextValue {
    return useContext(PushRegistrationContext)
}

export function serverStatusLabel(status: ServerRegistrationStatus): string {
    switch (status) {
        case 'registered':
            return 'Yes'
        case 'not_registered':
            return 'No'
        case 'not_configured':
            return 'Not configured'
        case 'auth_error':
            return 'API key error'
        case 'unavailable':
            return 'Unavailable'
        default:
            return '—'
    }
}

export function serverStatusIsError(status: ServerRegistrationStatus): boolean {
    return status === 'not_registered'
        || status === 'not_configured'
        || status === 'auth_error'
        || status === 'unavailable'
}
